import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navLinks = [
  { label: 'Guides', href: '#guides' },
  { label: 'Comparisons', href: '#comparison' },
  { label: 'Articles', href: '#articles' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    setCategoriesOpen(false);
    if (!isHome) {
      window.location.href = '/' + href;
      return;
    }
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const closeAll = () => {
    setMobileOpen(false);
    setCategoriesOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container-custom flex items-center justify-between h-16 md:h-18">
        <Link
          to="/"
          className="flex items-center gap-2 group"
          onClick={closeAll}
        >
          <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-lg"></i>
          </span>
          <span className={`font-heading font-semibold text-lg transition-colors ${
            scrolled ? 'text-foreground-900' : 'text-white'
          }`}>
            Superbuy <span className="text-primary-500">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(link.href);
              }}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-600'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </a>
          ))}

          <div className="relative group">
            <button
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('#categories');
              }}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-600'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              Categories
              <i className="ri-arrow-down-s-line text-xs"></i>
            </button>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            Superbuy Sheet
          </a>
        </div>

        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
            scrolled ? 'text-foreground-800 hover:bg-background-200' : 'text-white hover:bg-white/10'
          }`}
          onClick={() => {
            setMobileOpen(!mobileOpen);
            setCategoriesOpen(false);
          }}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-xl max-h-[85vh] overflow-y-auto">
          <div className="container-custom py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="flex items-center gap-3 text-foreground-700 hover:text-primary-600 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
              >
                {link.label === 'Guides' && <i className="ri-book-open-line text-base w-5 text-center"></i>}
                {link.label === 'Comparisons' && <i className="ri-scales-line text-base w-5 text-center"></i>}
                {link.label === 'Articles' && <i className="ri-article-line text-base w-5 text-center"></i>}
                {link.label}
              </a>
            ))}

            <button
              onClick={() => setCategoriesOpen(!categoriesOpen)}
              className="flex items-center justify-between gap-3 text-foreground-700 hover:text-primary-600 text-sm font-medium py-2.5 px-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer w-full text-left"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-base w-5 text-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${categoriesOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {categoriesOpen && (
              <div className="ml-8 mt-1 mb-2 border-l-2 border-background-200/70 pl-3 space-y-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeAll}
                    className="flex items-center gap-3 text-foreground-600 hover:text-primary-600 text-xs py-2 px-2 rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded bg-background-200/70 flex-shrink-0">
                      <i className={`${cat.icon} text-[10px]`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-200/60 my-2"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mx-3"
            >
              <i className="ri-external-link-line"></i>
              Superbuy Sheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeAll}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-full border-2 border-primary-500 text-primary-600 text-sm font-semibold hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap mx-3 mt-1"
            >
              <i className="ri-shopping-cart-line"></i>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}